import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import Stand from "../components/Stand";
import Coverage from "../components/Coverage";
import { siteData } from "../mock";

export default function AboutPage() {
  const { brand, aboutStory, aboutImages } = siteData;
  return (
    <>
      {/* Page header */}
      <section className="relative py-20 md:py-28 bg-jk-navy overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{backgroundImage: "radial-gradient(circle at 20% 20%, #c6a071 1px, transparent 1px)", backgroundSize: "28px 28px"}} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <div className="text-[11px] tracking-[0.32em] text-[#c6a071] mb-5">ABOUT JK ESTATES</div>
          <h1 className="font-serif text-white text-[44px] md:text-[76px] leading-[1.03] tracking-tight max-w-5xl">
            A decade of turning Tricity addresses <span className="italic text-[#c6a071]">into homes, offices and legacies.</span>
          </h1>
          <div className="mt-8 inline-flex items-center gap-3 bg-white/5 border border-white/15 px-5 py-3">
            <span className="w-2 h-2 rounded-full bg-[#c6a071]" />
            <span className="text-white/85 text-[13px] md:text-[14px] tracking-wide">{brand.trust}</span>
          </div>
        </div>
      </section>

      {/* Our story + images */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.32em] text-[#b88b4a] mb-4">{aboutStory.kicker}</div>
            <h2 className="font-serif text-neutral-900 text-[38px] md:text-[52px] leading-[1.06] tracking-tight">
              Born in Mohali, <span className="italic">built on trust.</span>
            </h2>
            <div className="mt-7 space-y-5 text-neutral-700 text-[15.5px] md:text-[16.5px] leading-[1.85]">
              {aboutStory.body.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/properties" className="btn-dark inline-flex items-center gap-2 px-6 py-3.5 text-[13px] tracking-wide">Explore Properties <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/contact" className="btn-outline-dark inline-flex items-center gap-2 px-6 py-3.5 text-[13px] tracking-wide">Talk to Us</Link>
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4 md:gap-5">
            {aboutImages.map((img) => (
              <figure key={img.src} className="group overflow-hidden border border-[#e7e2d7] bg-jk-cream">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                </div>
                <figcaption className="px-4 py-3 text-[12px] tracking-wide text-neutral-600">{img.alt}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="py-16 md:py-20 bg-jk-cream border-y border-[#e7e2d7]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { v: "2009", l: "Serving Tricity Since" },
            { v: "200+", l: "Satisfied Customers" },
            { v: "3", l: "Cities Covered" },
            { v: "4.9/5", l: "Client Trust Score" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-serif text-neutral-900 text-[44px] md:text-[56px] leading-none">{s.v}</div>
              <div className="mt-3 text-[10.5px] tracking-[0.28em] text-[#b88b4a]">{s.l.toUpperCase()}</div>
            </div>
          ))}
        </div>
      </section>

      <Stand />
      <Coverage />

      {/* Visit office */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <div className="text-[11px] tracking-[0.32em] text-[#b88b4a] mb-5">VISIT OUR OFFICE</div>
            <h2 className="font-serif text-neutral-900 text-[38px] md:text-[52px] leading-[1.05] tracking-tight">
              Let&apos;s meet <span className="italic">in person.</span>
            </h2>
            <p className="mt-6 text-neutral-700 text-[15.5px] md:text-[17px] leading-[1.85] max-w-lg">
              Drop by our Mohali office for a coffee and a genuine conversation about your property plans. No pitch, no pressure.
            </p>
            <div className="mt-7 flex gap-4">
              <MapPin className="w-5 h-5 text-[#b88b4a] mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <div className="text-neutral-900 text-[15.5px]">{brand.address}</div>
                <div className="text-neutral-600 text-[13.5px] mt-1">{brand.coverage}</div>
                <div className="text-neutral-600 text-[13.5px] mt-1">{brand.hours}</div>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`tel:${brand.primaryPhoneRaw}`} className="btn-gold inline-flex items-center gap-2 px-6 py-3.5 text-[13px] tracking-wide font-medium">
                <Phone className="w-4 h-4" /> Call {brand.primaryPhone}
              </a>
              <a href={`https://wa.me/${brand.whatsapp}`} target="_blank" rel="noopener noreferrer" className="btn-whatsapp inline-flex items-center gap-2 px-6 py-3.5 text-[13px] tracking-wide">
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="aspect-[16/10] w-full overflow-hidden border border-[#e7e2d7] bg-jk-cream">
              <iframe
                title="JK Estates Office Map"
                src="https://www.google.com/maps?q=Sector%2082A%20IT%20City%20Mohali&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
